<template>
  <div class="container py-5 my-registrations">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="mb-0">Mes inscriptions</h2>
      <b-button variant="outline-primary" size="sm" @click="fetchRegistrations" :disabled="loading">
        <b-icon icon="arrow-clockwise" class="mr-1" />
        Rafraîchir
      </b-button>
    </div>

    <b-alert v-if="error" variant="danger" show class="mb-4">
      {{ error }}
    </b-alert>

    <div v-if="loading" class="text-center py-5">
      <b-spinner label="Chargement..." />
    </div>

    <b-alert
      v-else-if="!registrations.length"
      variant="info"
      show
      class="text-center"
    >
      Vous n'êtes inscrit à aucune ouverture pour le moment.
    </b-alert>

    <div v-else>
      <section class="mb-5">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h4 class="mb-0">Ouvertures à venir</h4>
          <small class="text-muted">{{ upcomingRegistrations.length }} inscription(s)</small>
        </div>
        <b-alert v-if="!upcomingRegistrations.length" variant="info" show>
          Aucune ouverture future pour le moment.
        </b-alert>
        <div v-else class="table-responsive">
          <table class="table table-hover align-middle d-none d-md-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Titre</th>
                <th>Matériel demandé</th>
                <th>Covoiturage</th>
                <th class="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="registration in upcomingRegistrations" :key="registration.id">
                <td>{{ formatDate(registration.date) }}</td>
                <td>
                  {{ registration.title || "-" }}
                  <b-badge
                    v-if="registration.status === 'pending'"
                    variant="warning"
                    class="ml-1"
                  >
                    En attente de validation
                  </b-badge>
                </td>
                <td>{{ formatGear(registration) }}</td>
                <td>{{ formatCarpool(registration) }}</td>
                <td class="text-right">
                  <b-button
                    size="sm"
                    variant="outline-primary"
                    :disabled="registration.opening_loading || isCheckedOut(registration)"
                    :title="isCheckedOut(registration) ? 'Matériel déjà retiré, modification impossible.' : 'Modifier mon inscription'"
                    @click="openManageModal(registration, 0)"
                  >
                    <b-spinner v-if="registration.opening_loading" small class="mr-1" />
                    <b-icon v-else icon="pencil" class="mr-1"></b-icon>
                    Modifier
                  </b-button>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="d-md-none">
            <b-card
              v-for="registration in upcomingRegistrations"
              :key="`upcoming-card-${registration.id}`"
              class="registration-mobile-card mb-3"
            >
              <div class="mb-2">
                <div class="registration-mobile-card__title">
                  {{ registration.title || "-" }}
                  <b-badge
                    v-if="registration.status === 'pending'"
                    variant="warning"
                    class="ml-1"
                  >
                    En attente
                  </b-badge>
                </div>
                <div class="text-muted small">{{ formatDate(registration.date) }}</div>
              </div>
              <div class="registration-mobile-card__meta">
                <div class="registration-mobile-card__row">
                  <span class="registration-mobile-card__label">Matériel demandé</span>
                  <span class="registration-mobile-card__value">{{ formatGear(registration) }}</span>
                </div>
                <div class="registration-mobile-card__row">
                  <span class="registration-mobile-card__label">Covoiturage</span>
                  <span class="registration-mobile-card__value">{{ formatCarpool(registration) }}</span>
                </div>
              </div>
              <div class="mt-3 text-right">
                <b-button
                  size="sm"
                  variant="outline-primary"
                  :disabled="registration.opening_loading || isCheckedOut(registration)"
                  :title="isCheckedOut(registration) ? 'Matériel déjà retiré, modification impossible.' : 'Modifier mon inscription'"
                  @click="openManageModal(registration, 0)"
                >
                  <b-spinner v-if="registration.opening_loading" small class="mr-1" />
                  <b-icon v-else icon="pencil" class="mr-1"></b-icon>
                  Modifier
                </b-button>
              </div>
            </b-card>
          </div>
        </div>
      </section>

      <section>
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h4 class="mb-0">Ouvertures passées</h4>
          <small class="text-muted">{{ pastRegistrations.length }} inscription(s)</small>
        </div>
        <b-alert v-if="!pastRegistrations.length" variant="light" show>
          Aucune ouverture passée pour le moment.
        </b-alert>
        <div v-else class="table-responsive">
          <table class="table table-hover align-middle d-none d-md-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Titre</th>
                <th>Matériel demandé</th>
                <th>Covoiturage</th>
                <th class="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="registration in pastRegistrations" :key="`past-${registration.id}`">
                <td>{{ formatDate(registration.date) }}</td>
                <td>{{ registration.title || "-" }}</td>
                <td>{{ formatGear(registration) }}</td>
                <td>
                  <span class="text-muted small">-</span>
                </td>
                <td class="text-right">
                  <b-button
                    size="sm"
                    variant="outline-danger"
                    :disabled="true"
                    title="Cette ouverture est passée, désinscription non disponible."
                  >
                    Se désinscrire
                  </b-button>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="d-md-none">
            <b-card
              v-for="registration in pastRegistrations"
              :key="`past-card-${registration.id}`"
              class="registration-mobile-card registration-mobile-card--past mb-3"
            >
              <div class="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <div class="registration-mobile-card__title">{{ registration.title || "-" }}</div>
                  <div class="text-muted small">{{ formatDate(registration.date) }}</div>
                </div>
                <b-button
                  size="sm"
                  variant="link"
                  class="p-0 text-danger registration-mobile-action-btn"
                  :disabled="true"
                  title="Cette ouverture est passée, désinscription non disponible."
                  aria-label="Se désinscrire indisponible"
                >
                  <b-icon icon="box-arrow-right"></b-icon>
                </b-button>
              </div>
              <div class="registration-mobile-card__meta">
                <div class="registration-mobile-card__row">
                  <span class="registration-mobile-card__label">Matériel demandé</span>
                  <span class="registration-mobile-card__value">{{ formatGear(registration) }}</span>
                </div>
                <div class="registration-mobile-card__row">
                  <span class="registration-mobile-card__label">Covoiturage</span>
                  <span class="registration-mobile-card__value text-muted">-</span>
                </div>
              </div>
            </b-card>
          </div>
        </div>
      </section>
    </div>

    <EventRegistrationModal
      v-if="manageEvent"
      :event="manageEvent"
      :visible="manageModalVisible"
      mode="manage"
      :initial-tab="manageInitialTab"
      @update:visible="onManageModalVisibility"
      @updated="onManageUpdated"
    />
  </div>
</template>

<script>
import OpeningService from '../services/opening.service';
import EventRegistrationModal from '../components/EventRegistrationModal.vue';
import session from '../store/session';

export default {
  name: "MyRegistrations",
  components: {
    EventRegistrationModal
  },
  data() {
    return {
      registrations: [],
      loading: true,
      error: null,
      manageModalVisible: false,
      manageEvent: null,
      manageInitialTab: 0
    };
  },
  mounted() {
    this.fetchRegistrations();
  },
  computed: {
    upcomingRegistrations() {
      return this.registrations.filter(
        registration => !this.isRegistrationLocked(registration)
      );
    },
    pastRegistrations() {
      return this.registrations.filter(registration =>
        this.isRegistrationLocked(registration)
      );
    }
  },
  methods: {
    async fetchRegistrations() {
      this.loading = true;
      this.error = null;
      try {
        const res = await OpeningService.getMyRegistration();
        if (res.status !== 200) {
          throw new Error("Impossible de récupérer vos inscriptions.");
        }
  
        this.registrations = this.normalizeRegistrations(res.data);
        console.log(this.registrations);
      } catch (err) {
        console.error("Erreur mes inscriptions :", err);
        this.error =
          err && err.message
            ? err.message
            : "Une erreur inattendue est survenue.";
      } finally {
        this.loading = false;
      }
    },
    formatDate(date) {
      if (!date) return "Date à confirmer";
      return new Date(date).toLocaleDateString("fr-FR", {
        weekday: "long",
        day: "numeric",
        month: "long"
      });
    },
    formatTimeRange(start, end) {
      if (!start && !end) return "Horaire à définir";
      if (start && end) return `${start} - ${end}`;
      return start || end;
    },
    getParticipantInfos(registration) {
      if (!registration || !registration.participantInfos) return {};
      return registration.participantInfos;
    },
    isCheckedOut(registration) {
      return this.getParticipantInfos(registration).checkedOut === true;
    },
    normalizeRegistrations(rawList) {
      const list = Array.isArray(rawList) ? rawList : [];
      return list.map(item => ({
        ...item,
        opening_loading: false
      }));
    },
    formatGear(registration) {
      const participant = this.getParticipantInfos(registration);
      const gear = participant.gear && typeof participant.gear === "object" ? participant.gear : participant;
      const parts = [];
      if (gear.harnais === true || gear.harness === true) parts.push("Harnais");
      if (gear.combi === true || gear.wetsuit === true) parts.push("Combinaison");
      if (gear.sail) parts.push(this.formatSail(gear.sail));
      if (gear.board) parts.push(this.formatBoard(gear.board));
      return parts.length ? parts.join(", ") : "Aucun besoin particulier";
    },
    formatSail(sail) {
      if (!sail || typeof sail !== "object") return "Voile";
      const parts = [];
      if (sail.guc_id) parts.push(sail.guc_id);
      if (sail.brand) parts.push(sail.brand);
      if (sail.size) parts.push(`${sail.size} m²`);
      return parts.length ? parts.join(" · ") : "Voile";
    },
    formatBoard(board) {
      if (!board || typeof board !== "object") return "Planche";
      const parts = [];
      if (board.brand) parts.push(board.brand);
      if (board.volume) parts.push(`${board.volume} L`);
      return parts.length ? parts.join(" · ") : "Planche";
    },
    formatCarpool(registration) {
      const participant = this.getParticipantInfos(registration);
      if (Number.isFinite(participant.covoit) && participant.covoit > 0) {
        return `Je propose ${participant.covoit} place(s)`;
      }
      if (participant.carpool && participant.carpool.willCarpool) {
        const seats = participant.carpool.seatsAvailable
          ? `${participant.carpool.seatsAvailable} place(s)`
          : "places à préciser";
        return `Je propose un covoiturage (${seats})`;
      }
      if (participant.carpool && participant.carpool.needsCarpool) {
        return "Je cherche un covoiturage";
      }
      return "Pas de covoiturage demandé";
    },
    isRegistrationLocked(registration) {
      const startDate = this.getRegistrationStartDate(registration);
      if (!startDate) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const openingDay = new Date(startDate);
      openingDay.setHours(0, 0, 0, 0);
      return openingDay.getTime() < today.getTime();
    },
    getRegistrationStartDate(registration) {
      if (!registration) return null;
      const date = registration.date;
      if (!date) return null;
      if (registration.startTime) {
        const precise = new Date(`${date}T${registration.startTime}`);
        if (!isNaN(precise.getTime())) {
          return precise;
        }
      }
      const fallback = new Date(date);
      if (isNaN(fallback.getTime())) {
        return null;
      }
      return fallback;
    },
    goToCovoit(registration) {
      this.$router.push({ name: 'my-carpools', query: { ouvertureId: String(registration.id) } });
    },
    async openManageModal(registration, tabIndex) {
      if (!registration || !registration.id || registration.opening_loading) return;
      registration.opening_loading = true;
      try {
        const res = await OpeningService.getById(registration.id);
        if (res.status !== 200 || !res.data) {
          throw new Error("Impossible de charger l'ouverture.");
        }
        const opening = res.data;
        const sortedOpeningEvents = Array.isArray(opening.openingEvents)
          ? [...opening.openingEvents].sort(
              (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime()
            )
          : [];
        const currentUserId = session.user ? String(session.user.id) : null;
        const participant = Array.isArray(opening.participants) && currentUserId
          ? opening.participants.find(p => {
              const userId = p.user && typeof p.user === "object" ? p.user._id : p.user;
              return userId && String(userId) === currentUserId;
            })
          : null;
        this.manageEvent = {
          id: registration.id,
          title: opening.title || registration.title,
          openingType: opening.openingType || "MEMBER_OPENING",
          openingDate: opening.date || registration.date,
          openingEvents: sortedOpeningEvents,
          description: opening.description || "",
          price: opening.price,
          isRegistered: true,
          participantInfos: participant || registration.participantInfos
        };
        this.manageInitialTab = tabIndex === 1 ? 1 : 0;
        this.manageModalVisible = true;
      } catch (err) {
        console.error("Erreur ouverture modal :", err);
        this.$bvToast.toast(
          err && err.message ? err.message : "Impossible d'ouvrir la fenetre de gestion.",
          { title: "Erreur", variant: "danger", solid: true }
        );
      } finally {
        registration.opening_loading = false;
      }
    },
    onManageModalVisibility(value) {
      this.manageModalVisible = value;
      if (!value) {
        this.manageEvent = null;
      }
    },
    onManageUpdated() {
      this.fetchRegistrations();
    }
  }
};
</script>

<style scoped>
.registration-mobile-card {
  border: 1px solid #eef1f5;
  border-radius: 0.75rem;
  box-shadow: none;
}

.registration-mobile-card--past {
  background: #f8f9fa;
}

.registration-mobile-card__title {
  font-weight: 600;
}

.registration-mobile-action-btn {
  line-height: 1;
}

.registration-mobile-card__meta {
  border-top: 1px solid #f1f3f6;
  padding-top: 0.5rem;
}

.registration-mobile-card__row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0.2rem 0;
}

.registration-mobile-card__label {
  color: #6b7280;
  font-size: 0.85rem;
  margin-right: 0.75rem;
}

.registration-mobile-card__value {
  font-weight: 600;
  text-align: right;
}
</style>
